import { classifyMeld, isWild, type CardId, type MeldContext } from '@rummy/engine';
import type { HandResult } from '@rummy/protocol';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { PlayingCard } from './PlayingCard.tsx';

export interface HandResultListProps {
  results: readonly HandResult[];
  /** Final holdings by player. Without it a row shows its score and no cards. */
  reveal: Readonly<Record<string, readonly CardId[]>> | null | undefined;
  /** Seat handles, resolved by the caller — the live table and the history
   *  panel look them up in different places. */
  handleFor: (player: string) => string;
  accountId: string;
  /** The hand's own rules and wild rank, which are not the live table's. */
  ctx: MeldContext;
}

/**
 * Every player's finished hand, laid out grouped, lowest score first (§6.1).
 *
 * Extracted from `HandSummary` so the history panel is not a second, drifting
 * rendering of the same thing. A hand read back an hour later has to show the
 * cards exactly as the summary did, or the two disagree and neither is
 * trustworthy.
 */
export function HandResultList({
  results,
  reveal,
  handleFor,
  accountId,
  ctx,
}: HandResultListProps) {
  const { t } = useI18n();
  const ordered = [...results].sort((a, b) => a.points - b.points);

  return (
    <ol className="results">
      {ordered.map((result) => {
        const revealed = reveal?.[result.player] ?? [];
        const groups = result.groups.length > 0 ? result.groups : [revealed];
        const grouped = new Set(groups.flat());
        const loose = revealed.filter((c) => !grouped.has(c));
        return (
          <li
            key={result.player}
            className={`results__row${result.player === accountId ? ' is-you' : ''}`}
          >
            <div className="results__meta">
              <span className="results__name">{handleFor(result.player)}</span>
              <span className={`tag tag--${result.outcome}`}>
                {t(`outcome.${result.outcome}` as MessageKey)}
              </span>
              <span className="results__points">{result.points}</span>
            </div>

            {revealed.length > 0 && (
              <div className="results__hand" dir="ltr">
                {groups.map((group, i) => {
                  // Only a group that really is a combination gets the
                  // underline. A hand that ended by attrition was never
                  // declared, and drawing a line under it would claim
                  // something nobody claimed.
                  const meld =
                    group.length >= ctx.config.minMeldSize ? classifyMeld(group, ctx) : null;
                  const kind =
                    meld?.kind === 'pure-sequence' ? 'pure' : meld === null ? 'none' : 'valid';
                  return (
                    <div key={i} className={`results__group results__group--${kind}`}>
                      {group.map((id) => (
                        <PlayingCard key={id} id={id} wild={isWild(id, ctx)} feature />
                      ))}
                    </div>
                  );
                })}
                {loose.length > 0 && (
                  <div className="results__group results__group--loose">
                    {loose.map((id) => (
                      <PlayingCard key={id} id={id} wild={isWild(id, ctx)} feature dimmed />
                    ))}
                  </div>
                )}
              </div>
            )}

            {result.outcome === 'lost' && result.bestPossible < result.points && (
              <p className="results__best">
                {t('summary.best', { points: result.bestPossible })}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
